import { computed, ComputedRef, Ref } from 'vue';
import { useVirtualizer, VirtualItem } from '@tanstack/vue-virtual';
import { isNil } from 'lodash-es';
import type {
  GridRawRowDataType,
  GridRowInstance,
  GridStyleOption,
  GridDetailRowOption,
  CustomVirtualizerOptions,
} from '../../modernGrid.types';

export const useVirtualScroll = <RowDataType extends GridRawRowDataType>({
  tableRows,
  gridRef,
  styleInfo,
  detailRowInfo,
  usesVariableHeightDetailRows,
}: {
  tableRows: ComputedRef<GridRowInstance<RowDataType>[]>;
  gridRef: Ref<HTMLElement | null>;
  styleInfo: ComputedRef<GridStyleOption>;
  detailRowInfo: ComputedRef<GridDetailRowOption>;
  usesVariableHeightDetailRows: ComputedRef<boolean>;
}) => {
  const defaultRowHeight =
    styleInfo.value.body?.rowHeight || styleInfo.value.body?.rowMinHeight || 0;

  const estimateSize = (index: number) => {
    const row = tableRows.value[index];
    if (!row) return 0;

    const isDetailRow =
      detailRowInfo.value.use &&
      (!isNil(row.original.isDetail) ? row.original.isDetail : row.depth > 0);
    // detailRowHeight가 설정되어 있으면 해당 값 사용, 아니면 기본 rowHeight 사용
    const detailRowHeight = detailRowInfo.value.detailRowHeight ?? defaultRowHeight;
    return isDetailRow ? detailRowHeight : defaultRowHeight;
  };

  // 측정 결과 캐시 - 동일한 높이에 대한 불필요한 상태 업데이트 방지
  const measureCache = new WeakMap<Element, number>();

  /**
   * 요소의 높이를 측정하는 함수
   * - 부동소수점 미세 차이로 인한 불필요한 상태 업데이트 방지 (순환 참조 방지)
   * - 1px 미만의 높이 변화는 무시하고 캐시된 값 반환
   */
  const measureElement = (el: Element | null): number => {
    if (!el) {
      return 0;
    }

    const height = el.getBoundingClientRect().height;
    const cachedHeight = measureCache.get(el);

    // 캐시된 값이 있고, 1px 미만의 차이면 캐시된 값 반환 (순환 참조 방지)
    // getBoundingClientRect()의 부동소수점 미세 차이로 인한 무한 루프 방지
    if (cachedHeight !== undefined && Math.abs(height - cachedHeight) < 1) {
      return cachedHeight;
    }

    // 유의미한 높이 변경 시에만 캐시 업데이트
    measureCache.set(el, height);

    return height;
  };

  const virtualRowManagerOptions = computed<CustomVirtualizerOptions>(() => {
    const rows = tableRows.value;

    return {
      count: rows.length,
      getScrollElement: () => gridRef.value,
      estimateSize,
      overscan: 5,
      measureElement,
      ...(usesVariableHeightDetailRows.value && {
        // 새 row model 스냅샷마다 extractor도 갱신해 같은 길이의 정렬/필터도 재배치한다.
        getItemKey: (index: number) => rows[index]?.id ?? index,
      }),
    };
  });

  const virtualRowManager = useVirtualizer(virtualRowManagerOptions);
  const virtualRows = computed<VirtualItem[]>(() => virtualRowManager.value.getVirtualItems());
  const totalRowsHeight = computed<number>(() => virtualRowManager.value.getTotalSize() || 50);

  return {
    totalRowsHeight,
    virtualRowManager,
    virtualRows,
  };
};
